import React, { useEffect, useState } from "react";
import Editor from "../../components/editor";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { useAppServices } from "../../../../hook/services";
import { useKnowledgebaseCustomizationInfo } from "../../../../context/knowledgebaseCustomization";

const CreateArticle = () => {
  const { articleId, companyId, categoryId } = useParams();
  const [KnowledgebaseCustomization] = useKnowledgebaseCustomizationInfo();
  const navigate = useNavigate();
  const AppService = useAppServices();
  const [articleName, setArticleName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [documentHtml, setDocumentHtml] = useState("");
  const [category, setCategory] = useState("");
  const [processing, setProcessing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [languageId, setLanguageId] = useState(
    KnowledgebaseCustomization?.languageId || "6936c576ac8e853aea250bb1"
  );

  const getData = async () => {
    setProcessing(true);
    const { response } = await AppService.knowledgebase_article.filter({
      query: `_id=${articleId}`,
    });
    if (response) {
      const data = response.data;
      setArticleName(data?.name);
      setShortDesc(data?.shortDescription);
      setDocumentHtml(data?.doc);
      setCategory(data?.category);
    } else {
      setArticleName("");
      setShortDesc("");
      setDocumentHtml("");
      setCategory("");
    }
    setProcessing(false);
  };

  useEffect(() => {
    setLanguageId(KnowledgebaseCustomization?.languageId || "6936c576ac8e853aea250bb1");
  }, [KnowledgebaseCustomization?.languageId]);

  const getCategories = async () => {
    try {
      const { response } = await AppService.knowledgebase_category.get_agency_categories({
        query: `languageId=${languageId}&companyId=${companyId}`,
      });
      const responseData = response?.data || [];
      setCategories(
        responseData?.map((cat) => ({
          label: cat.name,
          value: cat._id,
        }))
      );
    } catch (error) {
      console.error(error);
      setCategories([]);
    } finally {
    }
  };

  useEffect(() => {
    if (!categoryId) {
      getCategories();
    } else {
      setCategory(categoryId);
    }
  }, []);

  useEffect(() => {
    if (articleId) {
      getData();
    }
  }, [articleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: articleName,
        shortDescription: shortDesc,
        doc: documentHtml,
        languageId: languageId,
        companyId: companyId,
        category: category,
        createdBy: "AGENCY",
        ...(articleId && { _id: articleId }),
      };

      const { response } = await AppService.knowledgebase_article?.[
        articleId ? "update" : "create"
      ]({
        payload,
      });
      if (response) {
        toast.success(articleId ? "Article updated" : "Article created");
        navigate(-1);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    }
  };

  if (processing) {
    return (
      <div className="ca-page d-flex flex-column align-items-center justify-content-center py-5 text-secondary" style={{ minHeight: "60vh" }}>
        <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
        <span style={{ fontSize: "0.875rem" }}>Loading article…</span>
        <CaStyles />
      </div>
    );
  }

  return (
    <div className="ca-page">
      <div className="container-fluid px-4 py-4 ca-container">
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="ca-back-btn d-inline-flex align-items-center mb-4 bg-transparent border-0 p-0 text-secondary"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4 d-flex align-items-center gap-3">
          <span className="ca-header-icon d-inline-flex align-items-center justify-content-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {articleId ? (
                <>
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </>
              ) : (
                <>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </>
              )}
            </svg>
          </span>
          <div>
            <h3 className="ca-title mb-1">{articleId ? "Edit article" : "Create article"}</h3>
            <p className="ca-subtitle mb-0">
              {articleId
                ? "Update the article content, description, or category."
                : "Write a new article and publish it to your knowledgebase."}
            </p>
          </div>
        </header>

        <div className="ca-card">
          <form onSubmit={handleSubmit}>
            {!categoryId && (
              <div className="ca-field">
                <label className="ca-label">Select category</label>
                <Select
                  required
                  options={categories}
                  value={categories?.find((item) => item.value == category)}
                  onChange={(e) => setCategory(e.value)}
                  classNamePrefix="ca-rs"
                  styles={selectStyles}
                />
              </div>
            )}

            <div className="ca-field">
              <label className="ca-label">Article name</label>
              <input
                type="text"
                className="ca-input"
                placeholder="Enter article name"
                value={articleName}
                onChange={(e) => setArticleName(e.target.value)}
                required
              />
            </div>

            <div className="ca-field">
              <label className="ca-label">Short description</label>
              <textarea
                className="ca-input ca-textarea"
                rows="3"
                placeholder="Enter short description"
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="ca-field">
              <label className="ca-label">Article content</label>
              <div className="ca-editor-wrap">
                <Editor
                  handleChange={(data) => setDocumentHtml(data)}
                  data={documentHtml}
                />
              </div>
            </div>

            <div className="ca-actions">
              <button type="submit" className="ca-btn ca-btn--primary w-100">
                {articleId ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Update article
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create article
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CaStyles />
    </div>
  );
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? "#4f46e5" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(79, 70, 229, 0.1)" : "none",
    borderRadius: 8,
    "&:hover": { borderColor: state.isFocused ? "#4f46e5" : "#d1d5db" },
    fontSize: "0.875rem",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#f5f3ff"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
    fontSize: "0.875rem",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
    border: "1px solid #ececec",
  }),
};

const CaStyles = () => (
  <style>{`
    .ca-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    .ca-container { max-width: 1200px; margin: 0 auto; }

    .ca-back-btn { transition: color 0.15s ease; }
    .ca-back-btn:hover { color: #1f2937 !important; }
    .ca-back-btn:hover svg { transform: translateX(-2px); }
    .ca-back-btn svg { transition: transform 0.15s ease; }

    .ca-header-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: #eef2ff;
      color: #4f46e5;
      flex-shrink: 0;
    }
    .ca-title {
      font-size: 1.375rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      color: #1f2937;
      margin: 0;
      line-height: 1.3;
    }
    .ca-subtitle {
      font-size: 0.875rem;
      color: #6b7280;
      line-height: 1.55;
    }

    .ca-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 14px;
      padding: 24px;
    }

    .ca-field { margin-bottom: 18px; }
    .ca-field:last-of-type { margin-bottom: 22px; }
    .ca-label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #1f2937;
      margin-bottom: 6px;
    }
    .ca-input {
      width: 100%;
      height: 40px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 0 12px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    .ca-input::placeholder { color: #9ca3af; }
    .ca-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .ca-textarea {
      height: auto;
      padding: 10px 12px;
      line-height: 1.5;
      resize: vertical;
      min-height: 84px;
    }

    .ca-editor-wrap {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .ca-editor-wrap:focus-within {
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .ca-editor-wrap > .App { min-height: 0 !important; background: transparent !important; }
    .ca-editor-wrap .ck.ck-editor { width: 100%; }
    .ca-editor-wrap .ck.ck-editor__main { max-height: none; }
    .ca-editor-wrap .ck.ck-toolbar {
      border: none;
      border-bottom: 1px solid #ececec;
      border-radius: 8px 8px 0 0;
      background: #fafafa;
    }
    .ca-editor-wrap .ck-editor__main > .ck-editor__editable {
      border: none !important;
      box-shadow: none !important;
      min-height: 220px;
      max-height: 360px;
      padding: 14px 18px;
      font-family: inherit;
      font-size: 0.9rem;
      line-height: 1.6;
      color: #1f2937;
      background: #fff;
    }
    .ca-editor-wrap .ck-editor__main > .ck-editor__editable.ck-focused {
      border: none !important;
      box-shadow: none !important;
    }

    .ca-actions { padding-top: 6px; }

    .ca-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 9px 16px;
      border-radius: 9px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .ca-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
    .ca-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }

    .ca-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .ca-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
    }
    .ca-btn--ghost:active { transform: translateY(0); }

    .ca-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .ca-btn--primary:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 8px 18px rgba(79, 70, 229, 0.3);
    }
    .ca-btn--primary:active { transform: translateY(0); }
  `}</style>
);

export default CreateArticle;
